'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

import type { NavigationEntry } from '../types';

const MAX_ENTRIES = 50;

interface NavigationHistoryContextValue {
  getNavigationHistory: () => NavigationEntry[];
}

const NavigationHistoryContext =
  createContext<NavigationHistoryContextValue | null>(null);

interface NavigationHistoryProviderProps {
  children: ReactNode;
}

export function NavigationHistoryProvider({
  children,
}: NavigationHistoryProviderProps) {
  const entriesRef = useRef<NavigationEntry[]>([]);
  const previousPathnameRef = useRef<string | null>(null);
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    const entry: NavigationEntry = {
      pathname,
      fullUrl: typeof window !== 'undefined' ? window.location.href : pathname,
      timestamp: new Date().toISOString(),
      previousPathname: previousPathnameRef.current,
    };

    const entries = entriesRef.current;
    if (entries.length >= MAX_ENTRIES) {
      entries.shift();
    }
    entries.push(entry);

    previousPathnameRef.current = pathname;
  }, [pathname, searchParams]);

  const getNavigationHistory = useCallback((): NavigationEntry[] => {
    return [...entriesRef.current];
  }, []);

  return (
    <NavigationHistoryContext.Provider value={{ getNavigationHistory }}>
      {children}
    </NavigationHistoryContext.Provider>
  );
}

export function useNavigationHistory(): NavigationHistoryContextValue {
  const context = useContext(NavigationHistoryContext);
  if (!context) {
    throw new Error(
      'useNavigationHistory must be used within a NavigationHistoryProvider',
    );
  }
  return context;
}
